import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronDown, ChevronUp, Lock } from "lucide-react";

export default function DataSharing() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("data_sharing"));
  const [loading, setLoading] = useState(true);
  const [labels, setLabels] = useState({});
  const [coaches, setCoaches] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [draft, setDraft] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/consent/my-coaches");
      setLabels(data?.labels || {});
      setCoaches(data?.coaches || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const curScopes = (c) => draft[c.coach_id] || c.scopes;
  const toggle = (c, key, val) => setDraft((p) => ({ ...p, [c.coach_id]: { ...(p[c.coach_id] || { ...c.scopes }), [key]: val } }));
  const changed = (c) => { const d = draft[c.coach_id]; return d && Object.keys(d).some((k) => Boolean(d[k]) !== Boolean(c.scopes[k])); };

  const save = async (c) => {
    const d = draft[c.coach_id];
    if (!d) return;
    const diff = {};
    Object.keys(d).forEach((k) => { if (Boolean(d[k]) !== Boolean(c.scopes[k])) diff[k] = Boolean(d[k]); });
    if (!Object.keys(diff).length) return;
    setSavingId(c.coach_id);
    try {
      await api.patch(`/consent/coach/${c.coach_id}`, diff);
      setDraft((p) => { const cp = { ...p }; delete cp[c.coach_id]; return cp; });
      await load();
      toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingId(null); }
  };

  const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="data-sharing-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/account")} data-testid="ds-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("data_sharing")}</h1>
          <p className="text-sm text-muted-foreground">{t("data_sharing_sub")}</p>
        </div>
      </div>

      {loading ? <Spinner label={t("common.loading")} /> : coaches.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border p-10 text-center" data-testid="ds-empty">
          <Lock className="h-7 w-7 text-muted-foreground" />
          <p className="font-display text-base text-foreground">{t("data_sharing")}</p>
          <p className="max-w-sm text-sm text-muted-foreground">{t("data_sharing_none_available_body")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {coaches.map((c) => {
            const open = expanded === c.coach_id;
            const cur = curScopes(c);
            return (
              <div key={c.coach_id} className="overflow-hidden rounded-2xl border border-border bg-card" data-testid={`ds-coach-${c.coach_id}`}>
                <button onClick={() => setExpanded(open ? null : c.coach_id)} data-testid={`ds-coach-toggle-${c.coach_id}`} className="flex w-full items-center gap-3 p-4 text-left">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 font-display text-base text-primary">{(c.name || "?")[0].toUpperCase()}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate font-display text-sm text-foreground">{c.name}</span>
                      {c.is_inner_circle ? <span className="rounded-full bg-primary px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary-foreground">{t("data_sharing_ic_badge")}</span> : null}
                    </span>
                    <span className="block text-xs text-muted-foreground">{c.granted_count > 0 ? t("data_sharing_n_shared", { count: c.granted_count }) : t("data_sharing_none_shared")}</span>
                  </span>
                  {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </button>

                {open ? (
                  <div className="space-y-1 border-t border-border bg-secondary/30 p-4">
                    <p className="mb-2 text-xs leading-relaxed text-muted-foreground">{t("data_sharing_intro", { coachName: c.name })}</p>
                    {Object.keys(labels).map((sk) => (
                      <div key={sk} className="flex items-center gap-3 border-b border-border/60 py-2.5" data-testid={`ds-scope-${c.coach_id}-${sk}`}>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-foreground">{labels[sk].label}</span>
                          <span className="block text-xs text-muted-foreground">{labels[sk].sub}</span>
                        </span>
                        <Switch checked={Boolean(cur[sk])} onCheckedChange={(v) => toggle(c, sk, v)} data-testid={`ds-switch-${c.coach_id}-${sk}`} />
                      </div>
                    ))}
                    <Button onClick={() => save(c)} disabled={!changed(c) || savingId === c.coach_id} className="mt-3 w-full rounded-full" data-testid={`ds-save-${c.coach_id}`}>{t("data_sharing_save")}</Button>
                    {c.updated_at ? <p className="pt-2 text-center text-[11px] italic text-muted-foreground">{t("data_sharing_updated", { when: fmtWhen(c.updated_at) })}</p> : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
